import { useEffect, useMemo, useState } from 'react'
import type {
    InterleavedContent,
    MarketingTile,
    Maybe,
    ProductInterface
} from '@pylot-data/pylotschema'
import { Products } from '@pylot-data/fwrdschema'
import type { PLPFilterTypes } from '@corratech/pylot-filters-and-sort'
import { getKeyString } from '@corratech/pylot-utils'
import {
    sortSortingLabels,
    translateSortingLabels
} from '@pylot-data/api/utils/sortingLabels'
import getMarketingTiles from '@pylot-data/api/operations/get-marketing-tiles'

export const usePlpProducts = (
    isValidating: boolean,
    plpManager: PLPFilterTypes.PLPManager,
    productItems?: Maybe<Array<Maybe<ProductInterface>>>,
    initialProducts?: Maybe<Products>,
    productsResult?: Maybe<Products>,
    parsedInterleavedContent?: Maybe<Array<InterleavedContent>>
): {
    visibleProductData: Maybe<Products>
    productsToRender: Maybe<ProductInterface>[]
    marketingTiles: MarketingTile[]
} => {
    const defaultSort = productsResult?.sort_fields?.default ?? 'position'
    const [firstProducts, setFirstProducts] = useState(initialProducts)
    const [marketingTiles, setMarketingTiles] = useState<MarketingTile[]>([])
    const [visibleProductData, setVisibleProductData] = useState(
        initialProducts ?? {
            items: [],
            aggregations: []
        }
    )

    const initialProductsKey = useMemo(() => {
        //@ts-ignore
        return firstProducts?.items ? getKeyString(firstProducts.items) : ''
    }, [firstProducts])

    const visibleProductsKey = useMemo(() => {
        return visibleProductData?.items
            ? //@ts-ignore
              getKeyString(visibleProductData.items)
            : ''
    }, [visibleProductData?.items])

    useEffect(() => {
        async function localGetMarketingTiles() {
            if (parsedInterleavedContent?.length) {
                const marketingTiles = await getMarketingTiles(
                    parsedInterleavedContent
                )
                setMarketingTiles(marketingTiles)
            }
        }

        localGetMarketingTiles()
    }, [parsedInterleavedContent])

    /* New product set can be loaded from the cache immediately if:
     * - it is not the initial set
     * - it is the initial set and there are no filters or non-default sort applied (after reset)
     * The purpose of this condition is to prevent initialData from appearing while we filter collection
     */
    const canUpdateProducts = (items: Maybe<ProductInterface>[]) => {
        const newProductsKey = getKeyString(items)
        if (visibleProductsKey === newProductsKey) {
            return false // same products
        }
        const hasFilters = Object.keys(plpManager.plpState.filters).length > 0
        const sortAttributes = Object.keys(plpManager.plpState.sort)
        const hasDefaultSort =
            sortAttributes.length === 0 || sortAttributes.includes(defaultSort)
        return (
            initialProductsKey !== newProductsKey ||
            (!hasFilters && hasDefaultSort)
        )
    }

    // Depending on other variables than isValidating may cause rendering cycle
    useEffect(() => {
        if (!productItems || productItems.length === 0) {
            // this has been commented cause it was returning default results even if search results is 0
            // setVisibleProductData(productsResult!)
            return
        }

        const sortFields = productsResult && { ...productsResult.sort_fields }
        const translatedOptions = translateSortingLabels(
            productsResult ? productsResult.sort_fields?.options : []
        )
        const sortedOptions = sortSortingLabels(translatedOptions)

        // Update products from SWR cache if possible
        if (isValidating && canUpdateProducts(productItems)) {
            setVisibleProductData({
                ...productsResult,
                items: productItems,
                sort_fields: {
                    ...sortFields,
                    options: sortedOptions
                }
            })
        }
        // Update products (if it is a new set) after loading
        const newProductsKey = getKeyString(productItems)
        if (!isValidating && visibleProductsKey !== newProductsKey) {
            setVisibleProductData({
                ...productsResult,
                items: productItems,
                sort_fields: {
                    ...sortFields,
                    options: sortedOptions
                }
            })
        }

        // If no initial products are provided, we use the first loaded set as 'initial'
        if (!firstProducts) {
            setFirstProducts(productsResult)
        }
    }, [isValidating, productsResult]) // eslint-disable-line

    const productsToRender = useMemo(() => {
        let visibleProductDataItems: any = []

        if (visibleProductData?.items && visibleProductData.items.length) {
            visibleProductDataItems = visibleProductData.items
            visibleProductDataItems.map((vpdi: any) => {
                if (vpdi?.canonical_url) {
                    vpdi['url_key'] =
                        vpdi.canonical_url.split('/')[
                            vpdi.canonical_url.split('/').length - 2
                        ]
                }
                return vpdi
            })
        }
        return visibleProductData?.items && visibleProductData.items.length
            ? visibleProductDataItems
            : (productItems ?? [])
    }, [productItems, visibleProductData?.items])

    return {
        visibleProductData,
        //@ts-ignore
        productsToRender,
        marketingTiles
    }
}
